import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Gift, Copy, Check, Users, Send } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { supabase } from '@/lib/supabase';
import { renderInviteCard } from '@/utils/inviteCard';
import { shareCardWithFriend, inviteLink } from '@/utils/shareCard';

// «Пригласи друга»: друг получает +7 дней, ты — +30 дней, когда друг ОПЛАТИТ.
// Награда — только дни подписки и только на оплату (экономика «не в минус»).
export default function Invite() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [invited, setInvited] = useState(0);
  const [rewarded, setRewarded] = useState(0);

  const code = (profile as any)?.ref_code as string | undefined;
  const link = code ? `${window.location.origin}/?ref=${code}` : '';

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('referrals')
      .select('status')
      .eq('referrer_id', profile.id)
      .then(({ data }) => {
        const rows = data ?? [];
        setInvited(rows.length);
        setRewarded(rows.filter((r: any) => r.status === 'rewarded').length);
      });
  }, [profile?.id]);

  function copyLink() {
    if (!link) return;
    haptic('light');
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  // Приглашение уходит карточкой с Дофи, а не голым текстом (Слава 07.10.2026).
  // Кнопка под карточкой ведёт сразу в бота с реф-кодом.
  async function shareTelegram() {
    if (!code || !profile?.id || sending) return;
    haptic('medium');
    setSending(true);
    try {
      const text = 'Веду питание в Slava Health — приложение с ИИ-коучем. Заходи по ссылке, тебе +7 дней бесплатно 🎁';
      const blob = await renderInviteCard({ name: profile.first_name, refCode: code });
      await shareCardWithFriend(blob, {
        userId: profile.id,
        key: 'invite',
        caption: text,
        link: inviteLink(code),
        button: 'Забрать +7 дней',
        fileName: 'slava-health-invite.png',
      });
      supabase.from('share_events').insert({ user_id: profile.id, achievement_id: 'invite', channel: 'chat' }).then(() => {});
    } catch (e) {
      console.error('[invite share]', e);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg pb-8">
      <div className="max-w-md mx-auto px-5 pt-6">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 text-ink-muted hover:text-ink mb-6 text-sm"
        >
          <ChevronLeft size={18} /> Назад
        </button>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-glow to-sky-glow items-center justify-center mb-4">
            <Gift size={28} className="text-bg" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">Пригласи друга</h1>
          <p className="text-ink-muted leading-relaxed">
            Друг получит <b className="text-ink">+7 дней</b> бесплатно.
            Ты получишь <b className="text-emerald-glow">+30 дней</b>, когда друг оформит подписку.
          </p>
        </motion.div>

        {/* Статистика */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-3 mb-5"
        >
          <div className="card p-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-ink-dim text-xs mb-1">
              <Users size={13} /> Приглашено
            </div>
            <div className="font-display text-2xl font-bold">{invited}</div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-ink-dim text-xs mb-1">Заработано дней</div>
            <div className="font-display text-2xl font-bold text-emerald-glow">{rewarded * 30}</div>
          </div>
        </motion.div>

        {/* Ссылка */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-elevated border-emerald-glow/40 p-4 mb-4"
        >
          <div className="label mb-2">Твоя ссылка-приглашение</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 text-sm text-ink-muted truncate bg-bg-elevated rounded-lg px-3 py-2.5">
              {link || '…'}
            </div>
            <button
              onClick={copyLink}
              className="shrink-0 w-11 h-11 rounded-lg bg-emerald-glow/15 text-emerald-glow flex items-center justify-center active:scale-95 transition-transform"
              aria-label="Копировать"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={shareTelegram}
          disabled={sending}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-glow to-sky-glow text-bg font-display font-bold text-base shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <Send size={18} /> Поделиться в Telegram
        </motion.button>

        <p className="text-center text-[11px] text-ink-dim mt-4 leading-relaxed">
          Награда +30 дней начисляется автоматически, когда приглашённый оформит платную подписку.
          Себе пригласить нельзя.
        </p>
      </div>
    </div>
  );
}
